<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Payment Integrations' }}</h2>
      <p>Gateway credentials. A partner's <strong>SPECIFIC</strong> row overrides the <strong>GLOBAL</strong> default.</p>
    </div>
    <p-button label="New Integration" icon="pi pi-plus" (onClick)="openAdd()" />
  </div>

  <div class="card">
    <div class="toolbar">
      <p-iconfield iconPosition="left" class="search-field">
        <p-inputicon styleClass="pi pi-search" />
        <input type="text" pInputText placeholder="Search gateway, merchant or scope..." [ngModel]="search()"
          (ngModelChange)="search.set($event)">
      </p-iconfield>
      <span class="count">{{ filtered().length }} of {{ rows().length }}</span>
    </div>

    <p-table [value]="filtered()" [loading]="loading()" dataKey="id" [rows]="10"
      [paginator]="filtered().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
      <ng-template pTemplate="header">
        <tr>
          <th>Gateway</th>
          <th>Scope</th>
          <th>Merchant Id</th>
          <th>Mode</th>
          <th>Active</th>
          <th class="ta-r">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr>
          <td class="name">{{ gatewayLabel(r) }}</td>
          <td>
            @if (r.integrationType === 'GLOBAL' || r.partnerId == null) {
              <p-tag value="GLOBAL" severity="contrast" pTooltip="Default for all partners." />
            } @else {
              <span pTooltip="Overrides the GLOBAL default for this partner.">{{ scopeLabel(r) }}</span>
            }
          </td>
          <td class="mono">{{ r.merchantId || '—' }}</td>
          <td>
            <p-tag [value]="r.isLive ? 'Live' : 'Test'" [severity]="r.isLive ? 'success' : 'warn'" />
          </td>
          <td>
            <span class="active-toggle" (click)="toggleActive(r)" pTooltip="Click to toggle">
              <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
                [severity]="r.isActive ? 'success' : 'secondary'" />
            </span>
          </td>
          <td class="ta-r actions">
            <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
              pTooltip="Edit" (onClick)="openEdit(r)" />
            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
              pTooltip="Delete" (onClick)="remove(r)" />
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">
            <div class="state"><i class="pi pi-inbox"></i>
              <p>No integrations configured.</p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editing() ? 'Edit Integration' : 'New Integration'" styleClass="menu-dialog"
  [style]="{ width: '640px' }" [breakpoints]="{ '640px': '94vw' }">
  <form [formGroup]="form" class="menu-form">
    <div class="grid-2">
      <div class="field">
        <label>Gateway <span class="req">*</span></label>
        <p-select formControlName="gatewayId" [options]="gatewayOptions()" optionLabel="label"
          optionValue="value" [filter]="true" filterBy="label" placeholder="Select gateway" appendTo="body" />
        @if (form.get('gatewayId')?.touched && form.get('gatewayId')?.invalid) {
          <small class="err">Gateway is required</small>
        }
      </div>
      <div class="field">
        <label>Scope <span class="req">*</span></label>
        <p-select formControlName="integrationType" [options]="scopes" placeholder="Select scope" appendTo="body" />
        <small class="hint-muted">GLOBAL applies to all partners; SPECIFIC overrides for one.</small>
      </div>
    </div>

    @if (isSpecific) {
      <div class="field">
        <label>Partner <span class="req">*</span></label>
        <p-select formControlName="partnerId" [options]="partnerOptions()" optionLabel="label"
          optionValue="value" [filter]="true" filterBy="label" [showClear]="true"
          placeholder="Select partner" appendTo="body" />
        @if (form.get('partnerId')?.touched && form.get('partnerId')?.invalid) {
          <small class="err">Partner is required for a SPECIFIC integration</small>
        }
      </div>
    }

    <div class="field">
      <label>Merchant Id</label>
      <input pInputText type="text" formControlName="merchantId" placeholder="Gateway merchant / account id">
    </div>

    <!-- Secrets (masked, toggle to reveal) -->
    <div class="grid-2">
      <div class="field">
        <label>API Key</label>
        <div class="secret">
          <input pInputText [type]="showApiKey() ? 'text' : 'password'" formControlName="apiKey"
            autocomplete="off" placeholder="••••••••">
          <button type="button" class="eye" (click)="showApiKey.set(!showApiKey())" tabindex="-1">
            <i class="pi" [class.pi-eye]="!showApiKey()" [class.pi-eye-slash]="showApiKey()"></i>
          </button>
        </div>
      </div>
      <div class="field">
        <label>API Secret</label>
        <div class="secret">
          <input pInputText [type]="showApiSecret() ? 'text' : 'password'" formControlName="apiSecret"
            autocomplete="off" placeholder="••••••••">
          <button type="button" class="eye" (click)="showApiSecret.set(!showApiSecret())" tabindex="-1">
            <i class="pi" [class.pi-eye]="!showApiSecret()" [class.pi-eye-slash]="showApiSecret()"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Auth Token</label>
        <div class="secret">
          <input pInputText [type]="showAuthToken() ? 'text' : 'password'" formControlName="authToken"
            autocomplete="off" placeholder="••••••••">
          <button type="button" class="eye" (click)="showAuthToken.set(!showAuthToken())" tabindex="-1">
            <i class="pi" [class.pi-eye]="!showAuthToken()" [class.pi-eye-slash]="showAuthToken()"></i>
          </button>
        </div>
      </div>
      <div class="field">
        <label>Webhook Secret</label>
        <div class="secret">
          <input pInputText [type]="showWebhook() ? 'text' : 'password'" formControlName="webhookSecret"
            autocomplete="off" placeholder="••••••••">
          <button type="button" class="eye" (click)="showWebhook.set(!showWebhook())" tabindex="-1">
            <i class="pi" [class.pi-eye]="!showWebhook()" [class.pi-eye-slash]="showWebhook()"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="field">
      <label>Extra Config (JSON)</label>
      <textarea class="form-textarea mono" rows="3" formControlName="extraConfigJson"
        placeholder='{ "theme": "dark" }'></textarea>
      <small class="hint-muted">Optional gateway-specific extras. Must be valid JSON.</small>
    </div>

    <div class="switch-grid">
      <label class="switch-row">
        <p-toggleSwitch formControlName="isLive" />
        <span>Live mode <small>(off = test / sandbox)</small></span>
      </label>
      <label class="switch-row">
        <p-toggleSwitch formControlName="isActive" />
        <span>Active</span>
      </label>
    </div>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
      <p-button label="Save" (onClick)="save()" [loading]="saving()" />
    </div>
  </form>
</p-dialog>
